import React from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaExternalLinkAlt,
  FaFileImage,
  FaMedal,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { GiPodium } from "react-icons/gi";

import {
  DEFAULT_RESULT_IMAGE,
  formatResultDate,
  formatResultDay,
  formatResultMonth,
  formatResultYear,
  getPalmaresStatusKey,
  getResultTokenClass,
  getResultTokenLabel,
  getResultValue,
  parseResultTokens,
  hasResult,
} from "../utils/resultatsUtils";

function ResultMeta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-200" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function ResultTokens({ resultats }) {
  const tokens = parseResultTokens(resultats);

  if (tokens.length === 0) {
    return (
      <span className="text-sm font-medium text-slate-500 dark:text-white/50">
        Aucun combat renseigné.
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {tokens.map((token, index) => (
        <span
          key={`${token}-${index}`}
          title={getResultTokenLabel(token)}
          className={`inline-flex h-9 min-w-9 items-center justify-center rounded-2xl px-3 text-sm font-bold ${getResultTokenClass(
            token,
          )}`}
        >
          {token}
        </span>
      ))}
    </div>
  );
}

function ResultatEventCard({
  event,
  palmaresStatus = {},
  isDeleting = false,
  isAddingPalmares = false,
  onEdit,
  onAddToPalmares,
  onOpenLink,
  onImageClick,
  onDelete,
}) {
  const statusKey = getPalmaresStatusKey(event);
  const isInPalmares = Boolean(palmaresStatus[statusKey]);
  const resultExists = hasResult(event);

  const podiumUrl = event?.AdherentEvent?.podium_url;
  const tableauUrl = event?.AdherentEvent?.tableau_url;

  return (
    <article className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="relative min-h-56 overflow-hidden bg-slate-950">
        <img
          src={event?.photo_url || DEFAULT_RESULT_IMAGE}
          alt={event?.titre || "Résultat"}
          className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 group-hover:scale-105"
          onError={(eventImage) => {
            eventImage.currentTarget.src = DEFAULT_RESULT_IMAGE;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

        <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-xl">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-4 py-2 text-white">
            <p className="text-3xl font-bold leading-none">
              {formatResultDay(event?.start)}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em]">
              {formatResultMonth(event?.start)}
            </p>
          </div>
          <p className="px-3 py-1 text-xs font-bold text-slate-500">
            {formatResultYear(event?.start)}
          </p>
        </div>

        <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2">
          {resultExists ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-2 text-xs font-bold text-emerald-700 shadow-lg dark:bg-emerald-500/10 dark:text-emerald-200">
              <FaCheck />
              Résultat saisi
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white backdrop-blur-xl">
              À compléter
            </span>
          )}

          {isInPalmares && (
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-3 py-2 text-xs font-bold text-slate-950 shadow-lg">
              <FaMedal />
              Palmarès
            </span>
          )}
        </div>

        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h3 className="mt-3 line-clamp-2 font-titre text-2xl font-bold leading-tight">
            {event?.titre || "Compétition sans titre"}
          </h3>
        </div>
      </div>

      <div className="p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <ResultMeta
            icon={FaTrophyIcon}
            label="Classement"
            value={getResultValue(event, "classement")}
          />

          <ResultMeta
            icon={FaCalendarAlt}
            label="Date"
            value={formatResultDate(event?.start)}
          />
        </div>

        <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Résultats des combats
          </p>

          <div className="mt-3">
            <ResultTokens resultats={event?.AdherentEvent?.resultats} />
          </div>
        </div>

        <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Observations
          </p>
          <p className="mt-2 line-clamp-4 text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
            {getResultValue(event, "observations")}
          </p>
        </div>

        {(podiumUrl || tableauUrl) && (
          <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.05]">
            <div className="mb-3 flex items-center gap-2">
              <FaFileImage className="text-amber-700 dark:text-amber-200" />
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Documents associés
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {podiumUrl && (
                <button
                  type="button"
                  onClick={() => onImageClick(podiumUrl)}
                  className="group/document flex min-h-14 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left transition hover:-translate-y-0.5 hover:border-amber-200 dark:border-white/10 dark:bg-slate-950"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
                    <GiPodium />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-white">
                      Podium
                    </p>
                    <p className="text-xs font-medium text-slate-500 dark:text-white/45">
                      Voir l'image
                    </p>
                  </div>
                </button>
              )}

              {tableauUrl && (
                <button
                  type="button"
                  onClick={() => onOpenLink(tableauUrl)}
                  className="group/document flex min-h-14 items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left transition hover:-translate-y-0.5 hover:border-sky-200 dark:border-white/10 dark:bg-slate-950"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                    <FaTableList />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-white">
                      Tableau
                    </p>
                    <p className="text-xs font-medium text-slate-500 dark:text-white/45">
                      Ouvrir le lien
                    </p>
                  </div>
                  <FaExternalLinkAlt className="ml-auto text-xs text-slate-400" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="mt-5 grid gap-2 border-t border-slate-200 pt-5 dark:border-white/10 sm:grid-cols-3">
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-amber-500 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-amber-600"
          >
            <FaEdit />
            Modifier
          </button>

          <button
            type="button"
            onClick={onAddToPalmares}
            disabled={isAddingPalmares}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
          >
            {isInPalmares ? <FaEdit /> : <FaPlus />}
            {isAddingPalmares
              ? isInPalmares
                ? "Mise à jour..."
                : "Ajout..."
              : isInPalmares
                ? "Maj palmarès"
                : "Palmarès"}
          </button>

          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
          >
            <FaTrash />
            {isDeleting ? "Suppression..." : "Supprimer"}
          </button>
        </div>
      </div>
    </article>
  );
}

function FaTrophyIcon(props) {
  return <FaMedal {...props} />;
}

export default ResultatEventCard;
